import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  getStoredNotificationPreference,
  hasNotificationPromptDismissRecord,
  isNotificationPromptSuppressed,
  markNotificationPromptDismissed,
  reconcileNotificationPreference,
  requestNotificationPermission,
  setStoredNotificationPreference,
  showWelcomeNotification,
} from "@/lib/notifications";
import { subscribeWebPush, unsubscribeWebPush } from "@/lib/push";

type Theme = "light" | "dark";
export type CookieConsentLevel = "pending" | "all" | "essential" | "custom";

interface UiState {
  theme: Theme;
  cookieConsent: CookieConsentLevel;
  analyticsEnabled: boolean;
  notificationsEnabled: boolean;
  notificationPromptDismissed: boolean;
  /** @deprecated use cookieConsent !== "pending" */
  cookieAccepted: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  acceptAllCookies: () => void;
  acceptEssentialOnly: () => void;
  saveCustomPreferences: (analytics: boolean, notifications: boolean) => void;
  /** @deprecated use acceptAllCookies */
  acceptCookies: () => void;
  enableNotifications: () => Promise<boolean>;
  dismissNotificationPrompt: () => void;
  /** Alinha o estado com Notification.permission (ex.: usuário revogou no browser). */
  syncNotificationsFromBrowser: () => NotificationPermission | "unsupported";
}

function syncLegacyAccepted(consent: CookieConsentLevel): boolean {
  return consent !== "pending";
}

function readInitialPromptDismissed(): boolean {
  if (typeof window === "undefined") return false;
  return isNotificationPromptSuppressed();
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: "light",
      cookieConsent: "pending",
      analyticsEnabled: false,
      notificationsEnabled: getStoredNotificationPreference(),
      notificationPromptDismissed: readInitialPromptDismissed(),
      cookieAccepted: false,
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === "light" ? "dark" : "light" }),
      acceptAllCookies: () =>
        set({
          cookieConsent: "all",
          analyticsEnabled: true,
          cookieAccepted: true,
        }),
      acceptEssentialOnly: () =>
        set({
          cookieConsent: "essential",
          analyticsEnabled: false,
          cookieAccepted: true,
        }),
      saveCustomPreferences: (analytics, notifications) => {
        // Preferência de notificação só fica "enabled" após grant real do browser
        if (!notifications) {
          setStoredNotificationPreference(false);
          markNotificationPromptDismissed();
          set({
            cookieConsent: "custom",
            analyticsEnabled: analytics,
            cookieAccepted: true,
            notificationsEnabled: false,
            notificationPromptDismissed: true,
          });
          void unsubscribeWebPush();
          return;
        }
        set({
          cookieConsent: "custom",
          analyticsEnabled: analytics,
          cookieAccepted: true,
        });
      },
      acceptCookies: () => get().acceptAllCookies(),
      enableNotifications: async () => {
        const permission = await requestNotificationPermission();
        const granted = permission === "granted";
        if (granted) {
          set({
            notificationsEnabled: true,
            notificationPromptDismissed: true,
          });
          await subscribeWebPush();
          showWelcomeNotification();
        } else {
          set({ notificationsEnabled: false });
          // Recusou no diálogo nativo: não reperguntar a cada refresh
          markNotificationPromptDismissed();
          set({ notificationPromptDismissed: true });
          if (permission === "denied") {
            void unsubscribeWebPush();
          }
        }
        return granted;
      },
      dismissNotificationPrompt: () => {
        markNotificationPromptDismissed();
        set({ notificationPromptDismissed: true });
      },
      syncNotificationsFromBrowser: () => {
        const { permission, enabled, wasRevoked } = reconcileNotificationPreference();
        if (wasRevoked) {
          set({
            notificationsEnabled: false,
            notificationPromptDismissed: false,
          });
          void unsubscribeWebPush();
        } else {
          set({
            notificationsEnabled: enabled,
            // Só localStorage (janela de 7 dias) ou grant — não reaproveita flag Zustand vencida
            notificationPromptDismissed: enabled || isNotificationPromptSuppressed(),
          });
        }
        return permission;
      },
    }),
    {
      name: "eusebionews-ui",
      partialize: (state) => ({
        theme: state.theme,
        cookieConsent: state.cookieConsent,
        analyticsEnabled: state.analyticsEnabled,
        notificationsEnabled: state.notificationsEnabled,
        notificationPromptDismissed: state.notificationPromptDismissed,
        cookieAccepted: syncLegacyAccepted(state.cookieConsent),
      }),
      merge: (persisted, current) => {
        const saved = persisted as Partial<UiState> & { cookieAccepted?: boolean };
        const merged = { ...current, ...saved };
        let consent = merged.cookieConsent ?? "pending";

        if (consent === "pending" && saved.cookieAccepted) {
          consent = "all";
        }

        // Preferência persistida não pode contradizer a permissão real do browser
        const reconciled = reconcileNotificationPreference();
        const suppressed = isNotificationPromptSuppressed();
        // localStorage manda; após 7 dias suppressed=false e o prompt pode voltar
        let promptDismissed = reconciled.wasRevoked ? false : suppressed || reconciled.enabled;

        // Migra dismiss antigo que existia só no Zustand (sem chave no localStorage)
        if (
          !reconciled.wasRevoked &&
          !promptDismissed &&
          Boolean(merged.notificationPromptDismissed) &&
          !hasNotificationPromptDismissRecord()
        ) {
          markNotificationPromptDismissed();
          promptDismissed = true;
        }

        return {
          ...merged,
          cookieConsent: consent,
          cookieAccepted: consent !== "pending",
          analyticsEnabled:
            consent === "all"
              ? true
              : consent === "essential"
                ? false
                : Boolean(merged.analyticsEnabled),
          notificationsEnabled: reconciled.enabled,
          notificationPromptDismissed: promptDismissed,
        };
      },
    },
  ),
);
